<template>
  <AcademyLayout @enroll="openEnroll">
    <section
      class="mx-banner"
      @mouseenter="stopAuto"
      @mouseleave="startAuto"
    >
      <div class="mx-wrap">
        <div class="mx-banner__stage">
          <article
            v-for="(item, idx) in academyBanners"
            :key="item.id"
            class="mx-slide"
            :class="[`mx-slide--${item.tone}`, { 'mx-slide--active': idx === bannerIndex }]"
          >
            <div class="mx-slide__pattern" aria-hidden="true"></div>
            <div class="mx-slide__body">
              <p class="mx-slide__headline">{{ item.headline }}</p>
              <p v-for="(line, i) in item.lines" :key="i" class="mx-slide__line">{{ line }}</p>
              <button type="button" class="mx-slide__cta" @click="openEnroll">立即报名</button>
            </div>
          </article>

          <div class="mx-banner__dots">
            <button
              v-for="(item, idx) in academyBanners"
              :key="item.id"
              type="button"
              class="mx-dot"
              :class="{ 'mx-dot--active': idx === bannerIndex }"
              @click="bannerIndex = idx"
            />
          </div>

          <button type="button" class="mx-nav mx-nav--prev" aria-label="上一条" @click="prevBanner">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" d="M14.5 5.5 8 12l6.5 6.5" />
            </svg>
          </button>
          <button type="button" class="mx-nav mx-nav--next" aria-label="下一条" @click="nextBannerManual">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" d="M9.5 5.5 16 12l-6.5 6.5" />
            </svg>
          </button>
        </div>
      </div>
    </section>

    <div class="mx-wrap">
      <!-- 课程分类 -->
      <div class="mx-filter">
        <span class="mx-filter__label">课程分类</span>
        <div class="mx-filter__list">
          <button
            v-for="cat in courseCategories"
            :key="cat"
            type="button"
            class="mx-filter__btn"
            :class="{ 'is-active': activeCat === cat }"
            @click="activeCat = cat"
          >
            {{ cat }}
          </button>
        </div>
      </div>

      <!-- 魔学院风格：排序 + 筛选 -->
      <div class="mx-toolbar">
        <div class="mx-sort">
          <button
            v-for="opt in courseSortOptions"
            :key="opt.key"
            type="button"
            class="mx-sort__item"
            :class="{ 'is-active': sortKey === opt.key }"
            @click="toggleSort(opt.key)"
          >
            {{ opt.label }}
            <span v-if="opt.key !== 'latest'" class="mx-sort__arrows" aria-hidden="true">
              <i :class="{ on: sortKey === opt.key && sortAsc }"></i>
              <i :class="{ on: sortKey === opt.key && !sortAsc }"></i>
            </span>
          </button>
        </div>
        <div class="mx-free">
          <span>只看免费</span>
          <button
            type="button"
            class="mx-switch"
            :class="{ 'is-on': freeOnly }"
            role="switch"
            :aria-checked="freeOnly"
            @click="freeOnly = !freeOnly"
          >
            <i></i>
          </button>
        </div>
      </div>

      <div class="mx-panel">
        <div class="mx-panel__row mx-panel__row--split">
          <div class="mx-panel__group">
            <span class="mx-panel__label">学习状态：</span>
            <div class="mx-checks">
              <label v-for="s in learnStatusOptions" :key="s" class="mx-check">
                <input v-model="selectedStatus" type="checkbox" :value="s" />
                <span>{{ s }}</span>
              </label>
            </div>
          </div>
          <div class="mx-panel__group">
            <span class="mx-panel__label">课程包含：</span>
            <div class="mx-checks">
              <label v-for="inc in courseIncludeOptions" :key="inc" class="mx-check">
                <input v-model="selectedIncludes" type="checkbox" :value="inc" />
                <span>{{ inc }}</span>
              </label>
            </div>
          </div>
        </div>
        <div class="mx-panel__row mx-panel__row--tags">
          <span class="mx-panel__label">标签：</span>
          <div ref="tagBoxRef" class="mx-tagbox" :class="{ 'is-open': tagOpen }">
            <button type="button" class="mx-tagbox__trigger" @click="tagOpen = !tagOpen">
              <span :class="{ 'is-placeholder': !selectedTags.length }">
                {{ selectedTags.length ? selectedTags.join('、') : '请选择标签' }}
              </span>
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path fill="currentColor" d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
            </button>
            <div v-if="tagOpen" class="mx-tagbox__drop">
              <input
                v-model="tagQuery"
                type="search"
                class="mx-tagbox__search"
                placeholder="搜索标签"
              />
              <label v-for="tag in filteredTagOptions" :key="tag" class="mx-check">
                <input v-model="selectedTags" type="checkbox" :value="tag" />
                <span>{{ tag }}</span>
              </label>
              <p v-if="!filteredTagOptions.length" class="mx-tagbox__empty">无匹配标签</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 最新课程：多方向培训 -->
      <section class="mx-section">
        <div class="mx-section__head">
          <h2>最新课程</h2>
          <span>共 {{ filteredCourses.length }} 门</span>
        </div>
        <div v-if="filteredCourses.length" class="mx-grid">
          <article
            v-for="course in filteredCourses"
            :key="course.id"
            class="mx-card"
            @click="openCourse(course)"
          >
            <div class="mx-card__cover" :class="`mx-card__cover--${course.coverTone}`">
              <span class="mx-card__cat">{{ course.duration }}</span>
              <strong>{{ course.shortTitle || course.category }}</strong>
              <em v-if="course.free" class="mx-card__free">免费</em>
            </div>
            <div class="mx-card__body">
              <h3>{{ course.title }}</h3>
              <div class="mx-card__foot">
                <span class="mx-card__count">
                  <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                    <path fill="currentColor" d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8V22h19.2v-2.8c0-3.2-6.4-4.8-9.6-4.8z"/>
                  </svg>
                  {{ course.views }}
                </span>
                <span class="mx-card__tag mx-card__price">{{ formatCoursePrice(course.price) }}</span>
              </div>
              <div class="mx-card__tags">
                <span>{{ course.category }}</span>
                <span>{{ course.duration }}</span>
              </div>
            </div>
          </article>
        </div>
        <p v-else class="mx-empty">当前筛选条件下暂无课程</p>
      </section>

      <!-- 精选课程 -->
      <section class="mx-section">
        <div class="mx-section__head">
          <h2>精选课程</h2>
          <span>十天培训安排：工业视觉专项 ¥10,000 / 人</span>
        </div>
        <div class="mx-grid mx-grid--featured">
          <article
            v-for="course in featuredCourses"
            :key="`featured-${course.id}`"
            class="mx-card"
            @click="openCourse(course)"
          >
            <div class="mx-card__cover" :class="`mx-card__cover--${course.coverTone}`">
              <span class="mx-card__cat">{{ course.duration }}</span>
              <strong>{{ course.shortTitle || course.category }}</strong>
            </div>
            <div class="mx-card__body">
              <h3>{{ course.title }}</h3>
              <div class="mx-card__foot">
                <span class="mx-card__count">{{ course.summary }}</span>
                <span class="mx-card__tag">{{ course.learners }} 人学习</span>
              </div>
              <div class="mx-card__tags">
                <span v-for="tag in course.tags.slice(0, 3)" :key="tag">{{ tag }}</span>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>

    <AcademyEnrollModal v-model="enrollOpen" :course="null" />
  </AcademyLayout>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import AcademyLayout from '../components/AcademyLayout.vue'
import AcademyEnrollModal from '../components/AcademyEnrollModal.vue'
import {
  academyBanners,
  academyCourses,
  allCourseTags,
  courseIncludeOptions,
  courseSortOptions,
  featuredAcademyCourses,
  formatCoursePrice,
  learnStatusOptions,
} from '../data/trainingCatalog'

const STATUS_KEY = 'gaoren-academy-learn-status'

const router = useRouter()
const bannerIndex = ref(0)
const enrollOpen = ref(false)
const activeCat = ref('全部')
const sortKey = ref('latest')
const sortAsc = ref(false)
const freeOnly = ref(false)
const selectedStatus = ref([])
const selectedIncludes = ref([])
const selectedTags = ref([])
const tagQuery = ref('')
const tagOpen = ref(false)
const tagBoxRef = ref(null)
const learnStatusMap = ref({})
let timer = null

const courseCategories = computed(() => {
  const cats = [...new Set(academyCourses.map((c) => c.category))]
  return ['全部', ...cats]
})

const featuredCourses = computed(() => featuredAcademyCourses.slice(0, 4))

const filteredTagOptions = computed(() => {
  const q = tagQuery.value.trim()
  if (!q) return allCourseTags
  return allCourseTags.filter((t) => t.includes(q))
})

const filteredCourses = computed(() => {
  let list = academyCourses.filter((c) => {
    if (!c.latest) return false
    if (activeCat.value !== '全部' && c.category !== activeCat.value) {
      return false
    }
    if (freeOnly.value && !c.free) return false
    if (selectedStatus.value.length) {
      const st = learnStatusMap.value[c.id] || '未学习'
      if (!selectedStatus.value.includes(st)) return false
    }
    if (selectedIncludes.value.length) {
      const has = selectedIncludes.value.every((inc) => (c.includes || []).includes(inc))
      if (!has) return false
    }
    if (selectedTags.value.length) {
      const tags = c.tags || []
      if (!selectedTags.value.every((t) => tags.includes(t))) return false
    }
    return true
  })

  if (sortKey.value === 'latest') {
    return list
  }
  const key = sortKey.value
  return [...list].sort((a, b) => {
    const av = a[key] ?? 0
    const bv = b[key] ?? 0
    return sortAsc.value ? av - bv : bv - av
  })
})

watch(enrollOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
})

function loadLearnStatus() {
  try {
    const raw = localStorage.getItem(STATUS_KEY)
    if (raw) {
      learnStatusMap.value = JSON.parse(raw)
      return
    }
  } catch (_) { /* ignore */ }
  learnStatusMap.value = {
    'robot-program': '已学习',
    'drone-ops': '学习中',
    'plc-comm': '未学习',
    'agv-ops': '未学习',
    'vision-10day': '学习中',
  }
}

function toggleSort(key) {
  if (key === 'latest') {
    sortKey.value = 'latest'
    sortAsc.value = false
    return
  }
  if (sortKey.value === key) {
    sortAsc.value = !sortAsc.value
  } else {
    sortKey.value = key
    sortAsc.value = false
  }
}

function onDocClick(e) {
  if (!tagOpen.value || !tagBoxRef.value) return
  if (!tagBoxRef.value.contains(e.target)) tagOpen.value = false
}

function openEnroll() {
  enrollOpen.value = true
}

function openCourse(course) {
  const map = { ...learnStatusMap.value }
  if (!map[course.id] || map[course.id] === '未学习') {
    map[course.id] = '学习中'
    learnStatusMap.value = map
    try {
      localStorage.setItem(STATUS_KEY, JSON.stringify(map))
    } catch (_) { /* ignore */ }
  }
  router.push({ name: 'training-course', params: { courseId: course.id } })
}

function nextBanner() {
  bannerIndex.value = (bannerIndex.value + 1) % academyBanners.length
}

function prevBanner() {
  const len = academyBanners.length
  bannerIndex.value = (bannerIndex.value - 1 + len) % len
  startAuto()
}

function nextBannerManual() {
  nextBanner()
  startAuto()
}

function startAuto() {
  stopAuto()
  timer = window.setInterval(nextBanner, 4500)
}

function stopAuto() {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

onMounted(() => {
  loadLearnStatus()
  startAuto()
  document.addEventListener('click', onDocClick)
})
onUnmounted(() => {
  stopAuto()
  document.removeEventListener('click', onDocClick)
  document.body.style.overflow = ''
})
</script>

<style scoped>
.mx-wrap {
  width: min(1160px, calc(100% - 40px));
  margin: 0 auto;
}

.mx-banner {
  padding: 20px 0 4px;
}

.mx-banner__stage {
  position: relative;
  height: clamp(220px, 28vw, 300px);
  overflow: hidden;
  border-radius: 8px;
  background: #2b8fe0;
}

.mx-slide {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.45s ease;
  color: #fff;
  text-align: center;
}

.mx-slide--active {
  opacity: 1;
  pointer-events: auto;
  z-index: 1;
}

.mx-slide--blue { background: linear-gradient(145deg, #1a7fd4, #3a9ae8); }
.mx-slide--teal { background: linear-gradient(145deg, #0d8a9a, #1ab0c4); }
.mx-slide--navy { background: linear-gradient(145deg, #1a5f8a, #2a7fb0); }

.mx-slide__pattern {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 18% 28%, rgba(255, 255, 255, 0.14) 0, rgba(255, 255, 255, 0.14) 80px, transparent 82px),
    radial-gradient(circle at 82% 70%, rgba(255, 255, 255, 0.1) 0, rgba(255, 255, 255, 0.1) 100px, transparent 102px);
}

.mx-slide__body {
  position: relative;
  z-index: 1;
  padding: 24px 24px 40px;
  max-width: 720px;
}

.mx-slide__headline {
  margin: 0 0 10px;
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 800;
  letter-spacing: 0.04em;
}

.mx-slide__line {
  margin: 0;
  font-size: clamp(15px, 2vw, 20px);
  font-weight: 600;
  line-height: 1.45;
}

.mx-slide__cta {
  margin-top: 16px;
  padding: 9px 28px;
  border: none;
  border-radius: 4px;
  background: #fff;
  color: #2d8cf0;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.mx-banner__dots {
  position: absolute;
  left: 50%;
  bottom: 14px;
  z-index: 2;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
}

.mx-dot {
  width: 8px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.45);
  cursor: pointer;
}

.mx-dot--active {
  width: 22px;
  background: #fff;
}

.mx-nav {
  position: absolute;
  top: 50%;
  z-index: 3;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 50%;
  background: rgba(20, 40, 70, 0.32);
  color: #fff;
  cursor: pointer;
}

.mx-nav--prev { left: 12px; }
.mx-nav--next { right: 12px; }

.mx-section {
  margin: 28px 0 8px;
}

.mx-filter {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 20px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
}

.mx-filter__label {
  flex-shrink: 0;
  padding-top: 6px;
  font-size: 13px;
  font-weight: 700;
  color: #333;
}

.mx-filter__list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mx-filter__btn {
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid #e8e8e8;
  background: #fff;
  color: #666;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.mx-filter__btn:hover {
  border-color: #2d8cf0;
  color: #2d8cf0;
}

.mx-filter__btn.is-active {
  background: #2d8cf0;
  border-color: #2d8cf0;
  color: #fff;
  font-weight: 600;
}

.mx-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 12px;
  padding: 12px 16px;
  background: #f5f6f8;
  border-radius: 8px 8px 0 0;
}

.mx-sort {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.mx-sort__item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 14px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #666;
  font-size: 13px;
  cursor: pointer;
}

.mx-sort__item.is-active {
  background: #3a4454;
  color: #fff;
}

.mx-sort__arrows {
  display: inline-flex;
  flex-direction: column;
  gap: 1px;
  margin-left: 2px;
}

.mx-sort__arrows i {
  width: 0;
  height: 0;
  border-left: 3.5px solid transparent;
  border-right: 3.5px solid transparent;
  opacity: 0.35;
}

.mx-sort__arrows i:first-child {
  border-bottom: 4px solid currentColor;
}

.mx-sort__arrows i:last-child {
  border-top: 4px solid currentColor;
}

.mx-sort__arrows i.on {
  opacity: 1;
}

.mx-sort__item.is-active .mx-sort__arrows i {
  opacity: 0.45;
}

.mx-sort__item.is-active .mx-sort__arrows i.on {
  opacity: 1;
}

.mx-free {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  user-select: none;
}

.mx-switch {
  position: relative;
  width: 40px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: #c8cdd5;
  cursor: pointer;
  transition: background 0.2s ease;
}

.mx-switch i {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
  transition: transform 0.2s ease;
}

.mx-switch.is-on {
  background: #2d8cf0;
}

.mx-switch.is-on i {
  transform: translateX(18px);
}

.mx-panel {
  padding: 14px 16px 16px;
  background: #f5f6f8;
  border-radius: 0 0 8px 8px;
  margin-bottom: 8px;
}

.mx-panel__row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.mx-panel__row--split {
  display: flex;
  flex-wrap: wrap;
  gap: 20px 40px;
  margin-bottom: 14px;
}

.mx-panel__group {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
}

.mx-panel__label {
  flex-shrink: 0;
  padding-top: 2px;
  font-size: 13px;
  color: #666;
  white-space: nowrap;
}

.mx-checks {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
}

.mx-check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 13px;
  color: #555;
  cursor: pointer;
  user-select: none;
}

.mx-check input {
  width: 14px;
  height: 14px;
  margin: 0;
  accent-color: #2d8cf0;
  cursor: pointer;
}

.mx-panel__row--tags {
  align-items: center;
}

.mx-tagbox {
  position: relative;
  flex: 1;
  min-width: 0;
}

.mx-tagbox__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  min-height: 36px;
  padding: 6px 12px;
  border: 1px solid #d9dde5;
  border-radius: 6px;
  background: #fff;
  color: #333;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
}

.mx-tagbox__trigger .is-placeholder {
  color: #aaa;
}

.mx-tagbox__trigger svg {
  flex-shrink: 0;
  color: #999;
}

.mx-tagbox__drop {
  position: absolute;
  z-index: 20;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 220px;
  overflow: auto;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e0e3ea;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(30, 40, 60, 0.1);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mx-tagbox__search {
  width: 100%;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #e0e3ea;
  border-radius: 4px;
  font-size: 13px;
  outline: none;
}

.mx-tagbox__search:focus {
  border-color: #2d8cf0;
}

.mx-tagbox__empty {
  margin: 4px 0 0;
  font-size: 12px;
  color: #999;
}

.mx-empty {
  margin: 28px 0;
  text-align: center;
  font-size: 14px;
  color: #999;
}

.mx-card__free {
  position: absolute;
  right: 10px;
  bottom: 10px;
  padding: 2px 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.92);
  color: #2d8cf0;
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
}

.mx-section__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 14px;
}

.mx-section__head h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #222;
}

.mx-section__head span {
  font-size: 13px;
  color: #999;
  max-width: 55%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mx-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.mx-grid--featured {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.mx-grid--featured .mx-card__foot .mx-card__count {
  -webkit-line-clamp: 2;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  white-space: normal;
  overflow: hidden;
}

.mx-card {
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.mx-card:hover {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.mx-card__cover {
  position: relative;
  aspect-ratio: 16 / 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(145deg, #2d8cf0, #5babff);
}

.mx-card__cover--2 { background: linear-gradient(145deg, #19be6b, #47d08a); }
.mx-card__cover--3 { background: linear-gradient(145deg, #ff6a00, #ff9345); }
.mx-card__cover--4 { background: linear-gradient(145deg, #2b85e4, #17233d); }
.mx-card__cover--1 { background: linear-gradient(145deg, #2d8cf0, #5babff); }

.mx-card__cover strong {
  font-size: clamp(18px, 1.6vw, 22px);
  font-weight: 800;
  letter-spacing: 0.04em;
  padding: 0 12px;
  text-align: center;
  line-height: 1.3;
}

.mx-card__cat {
  position: absolute;
  left: 0;
  top: 10px;
  padding: 3px 8px;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 11px;
  border-radius: 0 4px 4px 0;
}

.mx-card__body {
  padding: 12px;
}

.mx-card__body h3 {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
  color: #333;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.9em;
}

.mx-card__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.mx-card__count {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #999;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mx-card__tag {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: #2d8cf0;
}

.mx-card__price {
  color: #ff6a00;
  font-weight: 700;
}

.mx-card__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.mx-card__tags span {
  font-size: 11px;
  color: #999;
  padding: 1px 6px;
  background: #f5f5f5;
  border-radius: 2px;
}

.mx-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.45);
}

.mx-day-dialog {
  position: relative;
  width: min(560px, 100%);
  max-height: min(85vh, 640px);
  overflow: auto;
  background: #fff;
  border-radius: 6px;
  padding: 24px 22px 22px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.mx-day-dialog__close {
  position: absolute;
  top: 10px;
  right: 12px;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: transparent;
  font-size: 22px;
  color: #999;
  cursor: pointer;
}

.mx-day-dialog__eyebrow {
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 600;
  color: #2d8cf0;
}

.mx-day-dialog h2 {
  margin: 0 0 8px;
  font-size: 18px;
  line-height: 1.4;
  color: #222;
  padding-right: 28px;
}

.mx-day-dialog__place {
  margin: 0 0 14px;
  font-size: 12px;
  color: #999;
}

.mx-day-dialog__content {
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 1.75;
  color: #666;
}

.mx-day-dialog__btn {
  width: 100%;
  padding: 12px 20px;
  border: none;
  border-radius: 4px;
  background: #ff6a00;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.mx-day-dialog__btn:hover {
  background: #e85f00;
}

@media (max-width: 1100px) {
  .mx-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .mx-wrap {
    width: calc(100% - 24px);
  }

  .mx-filter {
    flex-direction: column;
    gap: 10px;
  }

  .mx-toolbar {
    flex-direction: column;
    align-items: flex-start;
    border-radius: 8px 8px 0 0;
  }

  .mx-panel__row--split {
    flex-direction: column;
    gap: 12px;
  }

  .mx-grid,
  .mx-grid--featured {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
}

@media (max-width: 480px) {
  .mx-grid,
  .mx-grid--featured {
    grid-template-columns: 1fr;
  }
}
</style>
